import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Users, Target, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import type { EmiratizationSummaryView } from '../../../../models/payroll';
import { LawReference } from './LawReference';

interface EmiratizationCardProps {
  summary: EmiratizationSummaryView | null;
  loading?: boolean;
}

export const EmiratizationCard: React.FC<EmiratizationCardProps> = ({ summary, loading }) => {
  const { t } = useTranslation();

  if (loading || !summary) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {t('payroll.emiratization.loading', 'Loading Emiratization summary...')}
        </CardContent>
      </Card>
    );
  }

  const currentPct = (summary.currentRate * 100).toFixed(1);
  const targetPct = (summary.targetRate * 100).toFixed(1);
  const onTrack = summary.currentRate >= summary.targetRate;
  const progressPct = summary.targetRate > 0
    ? Math.min(100, (summary.currentRate / summary.targetRate) * 100)
    : 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-md bg-emerald-50 text-emerald-700">
            <Flag className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">
                {t('payroll.emiratization.title', 'Emiratization Compliance')}
              </h3>
              <LawReference
                citations={[
                  {
                    code: 'Cabinet Decision 18/2022',
                    description: t(
                      'payroll.complianceCenter.lawRef.emiratization',
                      'Private companies with 50+ skilled workers must increase Emiratization by 2% per year, reaching 10% by end of 2026. Non-compliance carries monthly penalties per missing UAE national.'
                    ),
                  },
                ]}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{summary.legalReference}</p>
          </div>
          {!summary.quotaApplies && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 inline-flex items-center gap-1">
              <Info className="w-3 h-3" />
              {t('payroll.emiratization.quotaNotApplicable', 'Quota does not apply (< 50 employees)')}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg border border-border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Users className="w-3.5 h-3.5" />
              {t('payroll.emiratization.totalWorkforce', 'Total Workforce')}
            </div>
            <p className="text-2xl font-bold font-mono">{summary.totalWorkforce}</p>
          </div>
          <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/40">
            <div className="flex items-center gap-1.5 text-xs text-emerald-700 mb-1">
              <Flag className="w-3.5 h-3.5" />
              {t('payroll.emiratization.uaeNationals', 'UAE Nationals')}
            </div>
            <p className="text-2xl font-bold font-mono text-emerald-700">{summary.uaeNationalCount}</p>
            <p className="text-xs text-emerald-700/80">{currentPct}%</p>
          </div>
          <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/40">
            <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-1">
              <Target className="w-3.5 h-3.5" />
              {t('payroll.emiratization.target', 'Target ({{year}})', { year: new Date().getFullYear() })}
            </div>
            <p className="text-2xl font-bold font-mono text-blue-700">{targetPct}%</p>
            <p className="text-xs text-blue-700/80">
              {t('payroll.emiratization.targetHires', '{{count}} UAE nationals', { count: summary.targetUaeHires })}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg border ${
              onTrack
                ? 'border-emerald-200 bg-emerald-50/40 text-emerald-700'
                : summary.gap > 0
                ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-border'
            }`}
          >
            <div className="flex items-center gap-1.5 text-xs mb-1">
              {onTrack ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5" />
              )}
              {t('payroll.emiratization.gap', 'Hires to target')}
            </div>
            <p className="text-2xl font-bold font-mono">
              {onTrack ? 0 : summary.gap}
            </p>
            <p className="text-xs">
              {onTrack
                ? t('payroll.emiratization.onTrack', 'On track')
                : t('payroll.emiratization.shortfall', 'Below target')}
            </p>
          </div>
        </div>

        {summary.quotaApplies && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{t('payroll.emiratization.progressLabel', 'Progress toward target')}</span>
              <span>{progressPct.toFixed(0)}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${onTrack ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold mb-2">
            {t('payroll.emiratization.breakdown', 'Workforce by Nationality')}
          </h4>
          {summary.nationalityBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('payroll.emiratization.noEmployees', 'No employees on record.')}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {summary.nationalityBreakdown.map(n => {
                const pct = summary.totalWorkforce > 0
                  ? (n.count / summary.totalWorkforce) * 100
                  : 0;
                const isUae = n.nationality.toUpperCase().includes('UAE')
                  || n.nationality.toUpperCase().includes('EMIRATES')
                  || n.nationality.toUpperCase().includes('EMIRATI');
                return (
                  <div
                    key={n.nationality}
                    className={`flex items-center justify-between rounded-md border px-3 py-2 ${
                      isUae ? 'border-emerald-200 bg-emerald-50/40' : 'border-border'
                    }`}
                  >
                    <span className="text-sm">{n.nationality}</span>
                    <span className="text-xs font-mono">
                      {n.count} <span className="text-muted-foreground">({pct.toFixed(1)}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
